import React, { useEffect, useState, useCallback } from "react";
import api, { downloadContractTermsReport } from "../../api";
import { toast } from "sonner";
import { FileDown, Layers } from "lucide-react";

const BRACKET_CLS = {
  "Fino a 5 anni": "bg-sky-100 text-sky-700",
  "Da 5 a 10 anni": "bg-amber-100 text-amber-700",
  "Oltre 10 anni": "bg-emerald-100 text-emerald-700",
};

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("it-IT") : null; } catch { return iso; } };

export const ContractTermsTab = () => {
  const [company, setCompany] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/contract-terms?company=${company}`).then((r) => setRows(r.data.rows || [])).catch(() => {}).finally(() => setLoading(false));
  }, [company]);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    setDownloading(true);
    try {
      await downloadContractTermsReport(company);
      toast.success("Report PDF scaricato");
    } catch { toast.error("Errore nel download del PDF"); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="contract-terms-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-[13px] font-semibold text-gray-600">Società</label>
        <select value={company} onChange={(e) => setCompany(e.target.value)} data-testid="contract-terms-company" className="border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]">
          <option value="all">Tutte</option>
          <option value="teleborsa">STUDIO LEGALE VINCI</option>
          <option value="sit">NOLIMITS srl</option>
        </select>
        <button onClick={exportPdf} disabled={downloading || rows.length === 0} data-testid="contract-terms-export-pdf" className="ml-auto flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
          <FileDown size={16} /> {downloading ? "Preparazione..." : "Scarica PDF"}
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">Caricamento...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Nessun dipendente.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full min-w-[960px]">
            <thead>
              <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-bold">Dipendente</th>
                <th className="text-left px-4 py-3 font-bold">CCNL</th>
                <th className="text-left px-4 py-3 font-bold">Livello</th>
                <th className="text-left px-4 py-3 font-bold">Anzianità</th>
                <th className="text-left px-4 py-3 font-bold">Fine rapporto</th>
                <th className="text-right px-4 py-3 font-bold">Prova</th>
                <th className="text-right px-4 py-3 font-bold">Preavviso</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.matricola} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-[14px] font-bold text-[#16345c]">{r.name}</div>
                    <div className="text-[11px] text-gray-500">{r.matricola}{r.dept ? ` · ${r.dept}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600 max-w-[220px] truncate" title={r.ccnl_name || ""}>{r.ccnl_name || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3">
                    {r.level
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full"><Layers size={12} /> {r.level}</span>
                      : <span className="text-gray-400 text-[13px]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.seniority_bracket
                      ? <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${BRACKET_CLS[r.seniority_bracket] || "bg-gray-100 text-gray-600"}`}>{r.seniority_bracket}{r.seniority_years != null ? ` · ${r.seniority_years} a` : ""}</span>
                      : <span className="text-gray-400 text-[13px]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[13px]" data-testid={`contract-end-${r.matricola}`}>
                    {r.contract_end
                      ? <span className="font-semibold text-[#16345c]">{fmtDate(r.contract_end)}</span>
                      : <span className="text-gray-400" title="Tempo indeterminato">— <span className="text-[11px]">(indet.)</span></span>}
                  </td>
                  <td className="px-4 py-3 text-right text-[13px] font-semibold text-[#16345c]">{r.probation_days ? `${r.probation_days} gg` : <span className="text-gray-400 font-normal">—</span>}</td>
                  <td className="px-4 py-3 text-right text-[13px] font-semibold text-[#16345c]" data-testid={`notice-days-${r.matricola}`}>{r.notice_days ? `${r.notice_days} gg` : <span className="text-gray-400 font-normal">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-3">Anzianità di servizio calcolata dalla data di assunzione alla cessazione (o a oggi). Assegna un livello dal profilo del dipendente per popolare prova e preavviso.</p>
    </div>
  );
};
